/**
 * Графики. Нарисованы вручную, без библиотеки, и это осознанный выбор.
 *
 * Причины две. Первая: здесь нужно ровно четыре формы — ряд во времени,
 * спарклайн, горизонтальные столбики и тепловая карта; библиотека добавила бы
 * сотни килобайт ради этого. Вторая важнее: график в этом продукте —
 * доказательство под утверждением, и он обязан молча показывать «здесь
 * измерений не было», а не рисовать ноль. Готовые библиотеки по умолчанию
 * делают ровно наоборот.
 *
 * Что здесь есть и почему:
 * — разрыв ряда остаётся разрывом (null не соединяется линией);
 * — подсказка показывает ТОЧНОЕ значение и изменение к предыдущему замеру —
 *   иначе график отвечает «примерно вот так», а вопрос был «сколько»;
 * — ряд сравнения рисуется пунктиром и серым: он фон, а не второй герой;
 * — по графику можно ходить с клавиатуры стрелками.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { DASH, dayFull, dayShort, decimal, num, signed } from "../lib/format";

// ── Общее ───────────────────────────────────────────────────────────────

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  values: (number | null)[];
  /** Линия по умолчанию; площадь — для одного главного ряда; столбики — для счётных величин. */
  kind?: "line" | "area" | "bar";
  /** Пунктир и пониженный вес — признак ряда сравнения. */
  compare?: boolean;
  /** Своё форматирование значения в подсказке; иначе общее для графика. */
  format?: (value: number) => string;
  /** Подписи дат этого ряда, если они отличаются (прошлый период). */
  dates?: string[];
}

/** Ширина берётся из настоящего размера контейнера: viewBox с фиксированной
 *  шириной растягивает и подписи тоже, и на узком экране они превращаются в
 *  кашу. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(720);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = () => setWidth(Math.max(240, Math.round(node.clientWidth)));
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** Круглые засечки: 0 / 2500 / 5000, а не 0 / 2317 / 4634. */
function niceTicks(min: number, max: number, count = 4): number[] {
  if (min === max) {
    const pad = Math.abs(min) > 1 ? Math.abs(min) * 0.1 : 1;
    min -= pad;
    max += pad;
  }
  const rawStep = (max - min) / Math.max(1, count - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const step = (normalized > 5 ? 10 : normalized > 2 ? 5 : normalized > 1 ? 2 : 1) * magnitude;
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = start; value <= end + step / 1000; value += step) {
    ticks.push(Math.abs(value) < step / 1000 ? 0 : Number(value.toFixed(10)));
  }
  return ticks;
}

/** Разбивает ряд на непрерывные куски. Разрыв остаётся разрывом: соединить
 *  его линией значит нарисовать измерение, которого не было. */
/**
 * Подписи оси считаются от ШАГА засечки, а не от величины числа.
 *
 * Иначе получается так: подписчиков 33 876, размах ряда — полсотни человек,
 * засечки встают через 50, и «округлённо до тысяч» превращает их все в одну и
 * ту же «34 тыс.». Ось, на которой четыре одинаковые подписи, не ось.
 */
function axisFormat(ticks: number[]): (value: number) => string {
  const step = ticks.length > 1 ? Math.abs(ticks[1] - ticks[0]) : Math.abs(ticks[0]) || 1;
  if (step >= 1_000_000) return (value) => `${decimal(value / 1_000_000, 1)} млн`;
  if (step >= 1_000) return (value) => `${num(Math.round(value / 1000))} тыс.`;
  if (step >= 1) return (value) => num(Math.round(value));
  const digits = Math.min(3, Math.max(1, Math.ceil(-Math.log10(step))));
  return (value) => decimal(value, digits);
}

function segments(values: (number | null)[], x: (i: number) => number, y: (v: number) => number) {
  const runs: { x: number; y: number }[][] = [];
  let current: { x: number; y: number }[] = [];
  values.forEach((value, index) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      if (current.length) runs.push(current);
      current = [];
      return;
    }
    current.push({ x: x(index), y: y(value) });
  });
  if (current.length) runs.push(current);

  const line = (run: { x: number; y: number }[]) =>
    run.map((point, index) => `${index ? "L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");

  return {
    paths: runs.filter((run) => run.length > 1).map(line),
    /** Точка-одиночка между разрывами: линией её не нарисовать. */
    singles: runs.filter((run) => run.length === 1).map((run) => run[0]),
    area: (baseline: number) =>
      runs
        .filter((run) => run.length > 1)
        .map(
          (run) =>
            `${line(run)} L${run[run.length - 1].x.toFixed(1)},${baseline.toFixed(1)} L${run[0].x.toFixed(
              1,
            )},${baseline.toFixed(1)} Z`,
        ),
  };
}

// ── Ряд во времени ──────────────────────────────────────────────────────

export function TimeSeriesChart({
  dates,
  series,
  height = 264,
  formatValue,
  formatTip,
  tipFooter,
  includeZero = false,
  emptyNote = "За период нет ни одного измерения.",
  ariaLabel,
}: {
  dates: string[];
  series: ChartSeries[];
  height?: number;
  /** Своё форматирование подписей оси; по умолчанию — по шагу засечки. */
  formatValue?: (value: number) => string;
  /** Форматирование значения в подсказке; по умолчанию — полное число. */
  formatTip?: (value: number) => string;
  /** Дополнительная строка под значениями в подсказке. */
  tipFooter?: (index: number) => ReactNode;
  includeZero?: boolean;
  emptyNote?: string;
  ariaLabel?: string;
}) {
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const gradientId = useId();

  const visible = series.filter((item) => item.values.some((value) => value !== null && value !== undefined));
  const all = visible.flatMap((item) => item.values).filter((v): v is number => v !== null && v !== undefined && !Number.isNaN(v));

  const hasBars = visible.some((item) => item.kind === "bar");
  const padding = { top: 14, right: 10, bottom: 26, left: 54 };
  const plotW = Math.max(10, width - padding.left - padding.right);
  const plotH = Math.max(10, height - padding.top - padding.bottom);

  const scale = useMemo(() => {
    if (!all.length) return null;
    let min = Math.min(...all);
    let max = Math.max(...all);
    // Ноль в шкалу насильно НЕ добавляется. У показателя уровня (подписчики,
    // просмотры) ось от нуля превращает любое движение в плоскую линию:
    // важен не абсолют, а размах. Там, где ноль — настоящая точка отсчёта
    // (столбики, приросты), его просит сама страница через includeZero.
    if (includeZero || hasBars) min = Math.min(min, 0);
    if (max < 0) max = 0;
    const ticks = niceTicks(min, max, 4);
    return { min: ticks[0], max: ticks[ticks.length - 1], ticks };
  }, [all.join(","), includeZero, hasBars]);

  const pointCount = dates.length;

  const x = useCallback(
    (index: number) => {
      if (pointCount <= 1) return padding.left + plotW / 2;
      if (hasBars) return padding.left + ((index + 0.5) / pointCount) * plotW;
      return padding.left + (index / (pointCount - 1)) * plotW;
    },
    [pointCount, plotW, hasBars],
  );
  const y = useCallback(
    (value: number) => {
      if (!scale) return padding.top + plotH;
      const span = scale.max - scale.min || 1;
      return padding.top + plotH - ((value - scale.min) / span) * plotH;
    },
    [scale, plotH],
  );

  const pick = useCallback(
    (clientX: number) => {
      const node = wrapRef.current;
      if (!node || pointCount === 0) return;
      const box = node.getBoundingClientRect();
      const local = clientX - box.left;
      const ratio = (local - padding.left) / (plotW || 1);
      const index = hasBars
        ? Math.floor(ratio * pointCount)
        : Math.round(ratio * (pointCount - 1));
      setActive(Math.min(pointCount - 1, Math.max(0, index)));
    },
    [pointCount, plotW, hasBars],
  );

  useEffect(() => {
    setActive(null);
  }, [dates.length]);

  if (!visible.length || !scale) {
    return (
      <div className="chart-empty">
        <span>{emptyNote}</span>
        <span className="faint">Здесь был бы ноль — но ноль и «не измерено» это разные вещи.</span>
      </div>
    );
  }

  // Подписей дат ровно столько, сколько помещается: даты, наезжающие друг на
  // друга, хуже, чем их отсутствие.
  const labelSlots = Math.max(2, Math.min(pointCount, Math.floor(plotW / 92)));
  const labelIndexes =
    pointCount <= labelSlots
      ? dates.map((_, index) => index)
      : Array.from({ length: labelSlots }, (_, slot) =>
          Math.round((slot / (labelSlots - 1)) * (pointCount - 1)),
        );

  const barWidth = hasBars ? Math.max(2, (plotW / pointCount) * 0.62) : 0;
  const tipValue = formatTip ?? ((value: number) => num(Math.round(value * 100) / 100));
  const axisValue = formatValue ?? axisFormat(scale.ticks);

  return (
    <div
      className="chart-frame"
      ref={wrapRef}
      style={{ height }}
      onPointerMove={(event) => pick(event.clientX)}
      onPointerLeave={() => setActive(null)}
    >
      <svg
        className="chart"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel ?? visible.map((item) => item.label).join(", ")}
        tabIndex={0}
        onBlur={() => setActive(null)}
        onKeyDown={(event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault();
            setActive((prev) => {
              const step = event.key === "ArrowRight" ? 1 : -1;
              const next = (prev === null ? (event.key === "ArrowRight" ? -1 : pointCount) : prev) + step;
              return Math.min(pointCount - 1, Math.max(0, next));
            });
          }
          if (event.key === "Escape") setActive(null);
        }}
      >
        <defs>
          <linearGradient id={`${gradientId}-area`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-area-top)" />
            <stop offset="100%" stopColor="var(--chart-area-bottom)" />
          </linearGradient>
        </defs>

        {scale.ticks.map((tick) => (
          <g key={tick}>
            <line
              className={tick === 0 && scale.min < 0 ? "zero-line" : "grid-line"}
              x1={padding.left}
              x2={width - padding.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text className="axis-label" x={padding.left - 8} y={y(tick) + 3.5} textAnchor="end">
              {axisValue(tick)}
            </text>
          </g>
        ))}

        {visible.map((item) => {
          if (item.kind === "bar") {
            return (
              <g key={item.key}>
                {item.values.map((value, index) =>
                  value === null || value === undefined ? null : (
                    <rect
                      key={index}
                      className={`bar${active !== null && active !== index ? " dimmed" : ""}`}
                      x={x(index) - barWidth / 2}
                      y={Math.min(y(value), y(0))}
                      width={barWidth}
                      height={Math.max(1, Math.abs(y(value) - y(0)))}
                      rx={Math.min(3, barWidth / 2)}
                      fill={item.color}
                      opacity={item.compare ? 0.42 : 1}
                    />
                  ),
                )}
              </g>
            );
          }
          const { paths, singles, area } = segments(item.values, x, y);
          return (
            <g key={item.key}>
              {item.kind === "area" &&
                area(y(scale.min)).map((path, index) => (
                  <path key={`a${index}`} d={path} fill={`url(#${gradientId}-area)`} stroke="none" />
                ))}
              {paths.map((path, index) => (
                <path
                  key={index}
                  className={`series-line${item.compare ? " compare" : ""}`}
                  d={path}
                  stroke={item.color}
                />
              ))}
              {/* Одиночный замер линией не нарисуешь — он остаётся точкой. */}
              {singles.map((point, index) => (
                <circle key={`s${index}`} cx={point.x} cy={point.y} r="3" fill={item.color} />
              ))}
            </g>
          );
        })}

        {active !== null && (
          <g>
            <line className="crosshair" x1={x(active)} x2={x(active)} y1={padding.top} y2={padding.top + plotH} />
            {visible.map((item) => {
              const value = item.values[active];
              if (value === null || value === undefined || item.kind === "bar") return null;
              return (
                <circle
                  key={item.key}
                  className="series-dot"
                  cx={x(active)}
                  cy={y(value)}
                  r={item.compare ? 3.5 : 4.5}
                  fill={item.color}
                />
              );
            })}
          </g>
        )}

        {labelIndexes.map((index) => (
          <text
            key={index}
            className="axis-label"
            x={x(index)}
            y={height - 8}
            textAnchor={index === 0 ? "start" : index === pointCount - 1 ? "end" : "middle"}
          >
            {dayShort(dates[index])}
          </text>
        ))}
      </svg>

      {active !== null && (() => {
        // Подсказка встаёт над точкой, но у верхней кромки переворачивается
        // вниз: иначе она вылезает за карточку и закрывает заголовок.
        const ys = visible
          .map((item) => item.values[active])
          .filter((value): value is number => value !== null && value !== undefined)
          .map(y);
        const anchor = ys.length ? Math.min(...ys) : padding.top;
        const below = anchor < 110;
        return (
        <div
          className="chart-tip"
          style={{
            left: Math.min(Math.max(x(active), 92), Math.max(92, width - 92)),
            top: below ? (ys.length ? Math.max(...ys) : padding.top) + 16 : anchor - 14,
            transform: below ? "translate(-50%, 0)" : "translate(-50%, -100%)",
          }}
        >
          <div className="tip-date">{dayFull(dates[active])}</div>
          {visible.map((item) => {
            const value = item.values[active];
            const previous = item.values.slice(0, active).reverse().find((v) => v !== null && v !== undefined);
            const format = item.format ?? tipValue;
            return (
              <div className="tip-row" key={item.key}>
                <span className="swatch" style={{ background: item.color }} />
                <span className="tip-label">
                  {item.label}
                  {item.compare && item.dates?.[active] && (
                    <span className="faint"> · {dayShort(item.dates[active])}</span>
                  )}
                </span>
                <span className="tip-value">
                  {value === null || value === undefined ? (
                    <span className="dash">{DASH}</span>
                  ) : (
                    <>
                      {format(value)}
                      {previous !== undefined && previous !== null && !item.compare && (
                        <span className="faint" style={{ fontWeight: 460, marginLeft: 6 }}>
                          {signed(value - previous, Number.isInteger(value - previous) ? 0 : 1)}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
            );
          })}
          {tipFooter && <div className="tip-foot">{tipFooter(active)}</div>}
        </div>
        );
      })()}
    </div>
  );
}

// ── Легенда ─────────────────────────────────────────────────────────────

export function ChartLegend({ series }: { series: ChartSeries[] }) {
  return (
    <div className="chart-legend">
      {series.map((item) => (
        <span className="legend-item" key={item.key} style={{ color: item.compare ? item.color : undefined }}>
          <i
            className={`swatch${item.compare ? " dashed" : ""}`}
            style={item.compare ? undefined : { background: item.color }}
          />
          <span style={{ color: "var(--text-dim)" }}>{item.label}</span>
        </span>
      ))}
    </div>
  );
}

// ── Спарклайн ───────────────────────────────────────────────────────────

/** Микрограф в карточке показателя. Отвечает на «какой формы кривая» —
 *  и ни на что больше: точное число стоит строкой выше. */
export function Sparkline({
  values,
  color = "var(--chart-1)",
  height = 34,
}: {
  values: (number | null)[];
  color?: string;
  height?: number;
}) {
  const points = values.filter((v): v is number => v !== null && v !== undefined);
  if (points.length < 2) return null;
  const width = 120;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const x = (index: number) => (index / (values.length - 1)) * width;
  const y = (value: number) => height - 3 - ((value - min) / span) * (height - 6);
  const { paths } = segments(values, x, y);
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      {paths.map((path, index) => (
        <path key={index} d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
}

// ── Горизонтальные столбики ─────────────────────────────────────────────

/** Сравнение величин по категориям. Для времени не годится — там ряд. */
export function BarList({
  items,
  formatValue = (value: number) => num(value),
  color = "var(--chart-1)",
}: {
  items: { label: string; value: number | null; color?: string; title?: string }[];
  formatValue?: (value: number) => string;
  color?: string;
}) {
  const max = Math.max(...items.map((item) => Math.abs(item.value ?? 0)), 1);
  return (
    <div className="barlist">
      {items.map((item) => (
        <div className="barlist-row" key={item.label} title={item.title}>
          <span className="barlist-label">{item.label}</span>
          <span className="barlist-track">
            <span
              className="barlist-fill"
              style={{
                width: `${(Math.abs(item.value ?? 0) / max) * 100}%`,
                background: item.color ?? color,
              }}
            />
          </span>
          <span className="barlist-value">{item.value === null ? DASH : formatValue(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Тепловая карта ──────────────────────────────────────────────────────

export function Heatmap({
  cells,
  weekdays,
}: {
  cells: { weekday: number; hour: number; posts: number; views_median: number | null; reliable: boolean }[];
  weekdays: string[];
}) {
  const byKey = new Map(cells.map((cell) => [`${cell.weekday}:${cell.hour}`, cell]));
  const reliable = cells.filter((cell) => cell.reliable && cell.views_median !== null);
  const max = Math.max(...reliable.map((cell) => cell.views_median ?? 0), 1);
  const min = Math.min(...reliable.map((cell) => cell.views_median ?? 0), max);

  return (
    <>
      <div className="heatmap-scroll">
        <div className="heatmap">
          <span />
          {Array.from({ length: 24 }, (_, hour) => (
            <span key={`h${hour}`} className="collabel">
              {hour % 3 === 0 ? hour : ""}
            </span>
          ))}
          {weekdays.map((day, weekday) => (
            <Row key={day} day={day} weekday={weekday} byKey={byKey} max={max} />
          ))}
        </div>
      </div>
      <div className="heatmap-scale">
        <span>{reliable.length ? num(Math.round(min)) : DASH}</span>
        <span
          className="ramp"
          style={{
            background:
              "linear-gradient(90deg, color-mix(in srgb, var(--chart-1) 16%, transparent), var(--chart-1))",
          }}
        />
        <span>{reliable.length ? num(Math.round(max)) : DASH}</span>
        <span style={{ marginLeft: 8 }}>медиана просмотров · серое — меньше четырёх публикаций</span>
      </div>
    </>
  );
}

function Row({
  day,
  weekday,
  byKey,
  max,
}: {
  day: string;
  weekday: number;
  byKey: Map<string, { posts: number; views_median: number | null; reliable: boolean }>;
  max: number;
}) {
  return (
    <>
      <span className="rowlabel">{day}</span>
      {Array.from({ length: 24 }, (_, hour) => {
        const cell = byKey.get(`${weekday}:${hour}`);
        // Клетка без надёжной выборки остаётся серой: именно так рождаются
        // советы «публикуйте в четыре утра» — из двух случайных публикаций.
        const unreliable = !cell || !cell.reliable || cell.views_median === null;
        const intensity = unreliable ? 0 : (cell.views_median ?? 0) / max;
        return (
          <span
            key={hour}
            className="cell"
            title={
              cell
                ? `${day}, ${hour}:00 — ${cell.posts} публ., медиана ${
                    cell.views_median === null ? DASH : num(cell.views_median)
                  }${cell.reliable ? "" : " (мало данных)"}`
                : `${day}, ${hour}:00 — публикаций не было`
            }
            style={{
              background: unreliable
                ? "var(--surface-3)"
                : `color-mix(in srgb, var(--chart-1) ${Math.round(16 + intensity * 84)}%, transparent)`,
            }}
          />
        );
      })}
    </>
  );
}

// ── Карточка графика ────────────────────────────────────────────────────

/**
 * Обёртка вокруг графика: заголовок, вопрос, на который он отвечает,
 * управление, легенда и четыре состояния — загрузка, ошибка, пусто, данные.
 * Состояния живут здесь, а не на каждой странице по-своему: «пусто» и
 * «сломалось» обязаны выглядеть одинаково во всём продукте.
 */
export function ChartCard({
  title,
  sub,
  controls,
  series,
  loading = false,
  error,
  footnote,
  height = 264,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  controls?: ReactNode;
  series?: ChartSeries[];
  loading?: boolean;
  error?: unknown;
  footnote?: ReactNode;
  height?: number;
  children: ReactNode;
}) {
  return (
    <section className="card chart-card">
      <header className="chart-head">
        <div className="chart-title">
          <h2>{title}</h2>
          {sub && <div className="chart-sub">{sub}</div>}
        </div>
        {controls && <div className="chart-controls">{controls}</div>}
      </header>

      {loading ? (
        <div className="skeleton" style={{ height, borderRadius: "var(--r-control)" }} aria-busy="true" />
      ) : error ? (
        <div className="error-box" role="alert">
          {error instanceof Error ? error.message : String(error)}
        </div>
      ) : (
        <>
          {children}
          {series && series.length > 1 && <ChartLegend series={series} />}
        </>
      )}

      {footnote && !loading && <p className="faint">{footnote}</p>}
    </section>
  );
}
